import type {
    CreationRecord,
    LyricsTimelineLine,
    LyricsTimelineSection,
} from '~/api/creations'

type AudioHistoryLikeRecord = CreationRecord & {
    _isCreditLockPlaceholder?: boolean
    category?: string
    result_type?: string
    create_type?: string
}

const MUSIC_RECORD_TYPES = new Set([
    'MUSIC_CREATION_GENERATION',
    'TEXT_TO_MUSIC',
    'MUSIC_GENERATE',
    'VOCAL_SONG',
    'ONLY_MUSIC',
])

const normalizeHistoryRecordType = (value: unknown) =>
    String(value || '')
        .trim()
        .toUpperCase()
        .replace(/-/g, '_')

export const isPodcastHistoryRecord = (record?: CreationRecord | null) =>
    record?.type === 'PODCAST_GENERATION' ||
    record?.name === 'PODCAST_GENERATION'

export const isMusicTimelineRecord = (record?: CreationRecord | null) =>
    record?.type === 'MUSIC_TO_TIMELINE' ||
    record?.name === 'MUSIC_TO_TIMELINE'

export const isMusicCreationRecord = (
    record?: AudioHistoryLikeRecord | null
) => {
    if (!record || record._isCreditLockPlaceholder) return false
    if (record.category === 'MUSIC' || record.result_type === 'AUDIO') {
        return true
    }
    const typeUpper = normalizeHistoryRecordType(record.type)
    if (MUSIC_RECORD_TYPES.has(typeUpper)) return true
    const createType = String(record.create_type || '')
        .trim()
        .toLowerCase()
        .replace(/-/g, '_')
    return createType === 'text_to_music'
}

export const isTextToAudioHistoryRecord = (
    record?: AudioHistoryLikeRecord | null
) => {
    if (!record || record._isCreditLockPlaceholder) return false
    const candidates = [
        record.type,
        record.name,
        record.create_type,
        (record as CreationRecord & { record_type?: string }).record_type,
    ]
    return candidates.some(
        candidate => normalizeHistoryRecordType(candidate) === 'TEXT_TO_AUDIO'
    )
}

export const isAudioHistoryRecord = (record?: AudioHistoryLikeRecord | null) =>
    isMusicCreationRecord(record) ||
    isPodcastHistoryRecord(record) ||
    isMusicTimelineRecord(record)

export const getAudioHistoryRequestParams = (
    record?: CreationRecord | null
): Record<string, any> => {
    const raw = record?.request_params
    if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
        return raw as Record<string, any>
    }
    if (typeof raw === 'string' && raw.trim()) {
        try {
            const parsed = JSON.parse(raw) as unknown
            if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
                return parsed as Record<string, any>
            }
        } catch {
            /* ignore */
        }
    }
    return {}
}

export const isAudioUrl = (url?: string) => {
    const lowerUrl = String(url || '').toLowerCase()
    return (
        lowerUrl.includes('.mp3') ||
        lowerUrl.includes('.wav') ||
        lowerUrl.includes('.m4a') ||
        lowerUrl.includes('.aac') ||
        lowerUrl.includes('.ogg') ||
        lowerUrl.includes('/audio/')
    )
}

export const isImageUrl = (url?: string) => {
    const lowerUrl = String(url || '').toLowerCase()
    return (
        lowerUrl.includes('.jpg') ||
        lowerUrl.includes('.jpeg') ||
        lowerUrl.includes('.png') ||
        lowerUrl.includes('.webp') ||
        lowerUrl.includes('.gif') ||
        lowerUrl.includes('/image/')
    )
}

export const getAudioRecordUrl = (record?: CreationRecord | null) =>
    record?.results?.find((url: string) => isAudioUrl(url)) ||
    getAudioHistoryRequestParams(record)?.mp3_url ||
    record?.results?.[1] ||
    record?.results?.[0] ||
    ''

export const getAudioCoverUrl = (record?: CreationRecord | null) =>
    record?.results?.find((url: string) => isImageUrl(url)) ||
    getAudioHistoryRequestParams(record)?.cover_url ||
    ''

export const getTimelineLines = (
    record?: CreationRecord | null
): LyricsTimelineLine[] => record?.lyrics_lines?.lines || []

export const getTimelineSections = (
    record?: CreationRecord | null
): LyricsTimelineSection[] => record?.lyrics_lines?.sections || []

export const getTimelinePreviewLines = (
    record?: CreationRecord | null,
    limit = 5
) => getTimelineLines(record).slice(0, limit)

export const getTimelineDurationMs = (record?: CreationRecord | null) =>
    record?.lyrics_lines?.duration_ms || 0

export const getTimelineCopyText = (record?: CreationRecord | null) => {
    if (record?.lyrics_lines?.lrc) {
        return record.lyrics_lines.lrc
    }
    const lines = getTimelineLines(record)
    return lines
        .map(
            line => `[${formatTimelineTimestamp(line.start_ms, true)}]${line.text}`
        )
        .join('\n')
}

export const getTimelineTitle = (record?: CreationRecord | null) =>
    record?.type_name || record?.name || 'Timeline'

export const getTimelineActiveLineIndex = (
    lines: LyricsTimelineLine[],
    currentMs: number
) => {
    if (!lines.length || currentMs < 0) return -1

    return lines.findIndex((line, index) => {
        const nextStart = lines[index + 1]?.start_ms
        // Keep the current line active until the next line starts so playback
        // can transition smoothly even if there is a gap between end_ms values.
        const effectiveEnd = nextStart || line.end_ms || Number.MAX_SAFE_INTEGER
        return currentMs >= line.start_ms && currentMs < effectiveEnd
    })
}

export const formatTimelineTimestamp = (
    ms: number,
    includeCentiseconds = false
) => {
    const safeMs = Math.max(0, Math.floor(ms || 0))
    const totalSeconds = Math.floor(safeMs / 1000)
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = totalSeconds % 60

    if (!includeCentiseconds) {
        return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
    }

    const centiseconds = Math.floor((safeMs % 1000) / 10)
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${String(centiseconds).padStart(2, '0')}`
}
